class AmplitudeExperiments {
    constructor() {
        this.experiment = null;
        this.isInitialized = false;
    }

    async init(deploymentKey) {
        try {
            if (typeof Experiment === 'undefined') {
                console.error('Experiment SDK not loaded');
                return false;
            }
            
            if (typeof amplitude === 'undefined') {
                console.error('Amplitude SDK not loaded');
                return false;
            }

            this.experiment = Experiment.initializeWithAmplitudeAnalytics(deploymentKey);
            await this.experiment.fetch();
            await this.experiment.start();
            
            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('Error initializing Amplitude experiments:', error);
            return false;
        }
    }

    getVariant(experimentKey) {
        if (!this.isInitialized || !this.experiment) {
            return null;
        }
        return this.experiment.variant(experimentKey);
    }

    trackExposure(experimentKey) {
        if (this.experiment) {
            this.experiment.exposure(experimentKey);
        }
    }

    processPayload(payload) {
        if (!payload) return;

        this.hideElements(payload.hideSelectors);
        this.showElements(payload.showSelectors);
        this.replaceText(payload.replaceText);
        this.replaceInnerHtml(payload.replaceInnerHtml);
        this.replaceImages(payload.replaceImages);
        this.replaceVideos(payload.replaceVideos);
        this.addCSS(payload.addCSS);
        this.addClasses(payload.addClasses);
        this.removeClasses(payload.removeClasses);
        this.showABTImages(payload.showABTImage);
        this.unselectSubscribeHV2Checkboxes(payload.unselectSubscribe);
    }

    showABTImages(isShow) {
        if (!isShow) return;
 
        this.waitForElement('img[data-dl-abt-src]', (element) => {
            const img = element;
            const abtSrc = img.getAttribute('data-dl-abt-src');
            if (!abtSrc) return;
            
            // 规范化 URL
            const fullSrc = abtSrc.startsWith('http') || abtSrc.startsWith('//') 
                ? abtSrc 
                : '//' + abtSrc.replace(/^\/+/, '');
            
            // 获取基础 URL（保留原有的版本参数）
            const baseSrc = fullSrc.split('?')[0];
            const urlParams = new URL(fullSrc.startsWith('//') ? 'https:' + fullSrc : fullSrc).searchParams;
            const version = urlParams.get('v') || '';
            
            // 生成响应式 srcset
            const widths = [352, 832, 1200, 1920, 2400];
            const srcset = widths
                .map(w => version 
                    ? `${baseSrc}?v=${version}&width=${w} ${w}w`
                    : `${baseSrc}?width=${w} ${w}w`
                )
                .join(', ');
            
            const newSrc = version 
                ? `${baseSrc}?v=${version}&width=2400`
                : `${baseSrc}?width=2400`;
            
            // 预加载新图片
            const preloadImg = new Image();
            preloadImg.loading = 'eager';
            
            // 加载完成后再替换
            preloadImg.onload = () => {
                // 保存原始 src 用于失败回退
                const oldSrc = img.src;
                
                // 设置过渡效果
                img.style.transition = 'opacity 0.15s ease-in-out';
                img.style.opacity = '0.3';
                
                // 短暂延迟后替换（让淡出效果可见）
                setTimeout(() => {
                    img.src = newSrc;
                    img.srcset = srcset;
                    
                    // 图片已预加载，立即显示
                    requestAnimationFrame(() => {
                        img.style.opacity = '1';
                    });
                }, 150);
            };
            
            // 加载失败处理
            preloadImg.onerror = () => {
                console.error('Failed to preload ABT image:', baseSrc);
            };
            
            // 开始预加载
            preloadImg.src = newSrc;
        });

        this.waitForElement('video.abt-video.hidden', (element) => {
            const abtVideo = element;
            // 移除 ABT 视频的 hidden 类
            abtVideo.classList.remove('hidden');
            
            // 查找同一父元素下的其他 video（不包含 abt-video 类的）
            const parent = abtVideo.parentElement;
            if (parent) {
                const siblingVideos = parent.querySelectorAll('video:not(.abt-video)');
                siblingVideos.forEach(siblingVideo => {
                    // 给相邻的视频添加 hidden 类
                    siblingVideo.classList.add('hidden');
                    // 暂停播放
                    siblingVideo.pause();
                });
                
                // 播放 ABT 视频
                abtVideo.play();
            }
        });
    }
    hideElements(selectors) {
        if (!selectors || !Array.isArray(selectors)) return;
        selectors.forEach(selector => {
            this.waitForElement(selector, (element) => {
                if (element) {
                    this.cleanupScrollListeners(element);
                    element.remove();
                }
            });
        });
    }

    showElements(selectors) {
        if (!selectors || !Array.isArray(selectors)) return;
        
        selectors.forEach(selector => {
            this.waitForElement(selector, (element) => {
                if (element) {
                    element.style.display = 'block';
                    element.style.opacity = '1';
                    element.style.visibility = 'visible';
                }
            });
        });
    }

    replaceText(textItems) {
        if (!textItems || !Array.isArray(textItems)) return;
        
        textItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element) {
                    element.style.transition = 'opacity 0.2s ease-in-out';
                    element.style.opacity = '0.5';
                    setTimeout(() => {
                        element.textContent = item.text;
                        element.style.opacity = '1';
                    }, 200);
                }
            });
        });
    }
    replaceInnerHtml(innerHtmlItems) {
        if (!innerHtmlItems || !Array.isArray(innerHtmlItems)) return;
        
        innerHtmlItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element) {
                    element.innerHTML = item.innerHtml;
                }
            });
        });
    }
    replaceImages(imageItems) {
        if (!imageItems || !Array.isArray(imageItems)) return;
        
        imageItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element) {
                const baseSrc = item.src.replace(/\?.*$/, '');
                const timestamp = Date.now();
                
                const srcset = [
                    `${baseSrc}?v=${timestamp}&width=352 352w`,
                    `${baseSrc}?v=${timestamp}&width=832 832w`,
                    `${baseSrc}?v=${timestamp}&width=1200 1200w`,
                    `${baseSrc}?v=${timestamp}&width=1920 1920w`,
                    `${baseSrc}?v=${timestamp}&width=3840 3840w`
                ].join(', ');
                
                element.style.transition = 'opacity 0.2s ease-in-out';
                element.style.opacity = '0.8';
                
                const preloadSrc = `${baseSrc}?v=${timestamp}&width=1200`;
                const newImg = new Image();
                newImg.crossOrigin = 'anonymous';
                newImg.loading = 'eager';
                
                newImg.onload = function() {
                    element.src = `${baseSrc}?v=${timestamp}&width=3840`;
                    element.srcset = srcset;
                    
                    if (!element.hasAttribute('sizes')) {
                        element.sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
                    }
                    
                    if (item.alt) element.alt = item.alt;
                    element.style.opacity = '1';
                };
                
                newImg.onerror = function() {
                    element.style.opacity = '1';
                };
                
                newImg.src = preloadSrc;
                }
            });
        });
    }

    replaceVideos(videoItems) {
        if (!videoItems || !Array.isArray(videoItems)) return;
        
        videoItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element) {
                    element.src = item.src;
                    if (item.poster) element.poster = item.poster;
                }
            });
        });
    }

    addCSS(cssItems) {
        if (!cssItems || !Array.isArray(cssItems)) return;
        
        cssItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element && item.styles) {
                    Object.keys(item.styles).forEach(property => {
                        element.style[property] = item.styles[property];
                    });
                }
            });
        });
    }

    addClasses(classItems) {
        if (!classItems || !Array.isArray(classItems)) return;
        
        classItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element && item.classes) {
                    item.classes.forEach(className => {
                        element.classList.add(className);
                    });
                }
            });
        });
    }

    removeClasses(classItems) {
        if (!classItems || !Array.isArray(classItems)) return;
        
        classItems.forEach(item => {
            this.waitForElement(item.selector, (element) => {
                if (element && item.classes) {
                    item.classes.forEach(className => {
                        element.classList.remove(className);
                    });
                }
            });
        });
    }

    /**
     * 取消 .dl-product-subscribe-h-v2-item 内默认勾选的 accessory 复选框
     * 移除 dl-selected，由 dl-product-subscribe-h-v2-item-cbx 监听 class 变化后同步样式与总价
     */
    unselectSubscribeHV2Checkboxes(shouldUnselect) {
        if (!shouldUnselect) return;

        const selector = '.dl-product-subscribe-h-v2-item .dl-checkbox-h.dl-selected';
        const removeSelected = (checkbox) => {
            checkbox?.classList.remove('dl-selected');
        };
        document.querySelectorAll(selector).forEach(removeSelected);
        this.waitForElement(selector, removeSelected);
    }

    async runExperiment(experimentKey, deploymentKey) {
        try {
            const initialized = await this.init(deploymentKey);
            if (!initialized) {
                return;
            }

            const variant = this.getVariant(experimentKey);      
            if (!variant || !variant.key || variant.key === 'off') {
                return;
            }
            
            this.trackExposure(experimentKey);
            if ((variant.key === 'treatment' || variant.key === 'control') && variant.payload) {
                this.processPayload(variant.payload);
            }

        } catch (error) {
            console.error('Error running experiment:', error);
        }
    }

    waitForElement(selector, callback, maxAttempts = 50, interval = 100) {
        let attempts = 0;
        
        const checkElement = () => {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(element => {
                    callback(element);
                });
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkElement, interval);
            } else {
                console.warn(`Element not found after ${maxAttempts} attempts: ${selector}`);
                callback(null);
            }
        };
        
        checkElement();
    }

}

window.AmplitudeExperiments = new AmplitudeExperiments();
